import { useI18n } from '@/i18n'
import { TZDateMini } from '@date-fns/tz'
import { computedAsync, useNow } from '@vueuse/core'
import {
  addSeconds,
  format,
  formatDistanceToNow,
  formatISO,
  type Locale,
  max,
  parseISO,
} from 'date-fns'
import { enUS as DateFnEnUsLocale } from 'date-fns/locale/en-US'
import {
  computed,
  ComputedRef,
  isReadonly,
  isRef,
  MaybeRefOrGetter,
  Ref,
  toValue,
  WritableComputedRef,
} from 'vue'

const dateFnLocales: Record<string, () => Promise<Locale>> = {
  de: () => import('date-fns/locale/de').then((exp) => exp.de),
}

export function ensureDate(date: Date | string) {
  return typeof date === 'string' ? parseISO(date) : date
}

export function calculateDurationSeconds(
  start: Date | string,
  end: Date | string,
  absolute = true,
): number {
  const value = (ensureDate(end).getTime() - ensureDate(start).getTime()) / 1000
  return absolute ? Math.abs(value) : value
}

export function secondsToDurationString(seconds: number): string {
  const isNegative = seconds < 0
  const _seconds = Math.abs(Math.round(seconds))
  if (_seconds === 0) return `0s`
  const h = Math.floor(_seconds / 3600)
  const m = Math.floor((_seconds % 3600) / 60)
  const s = Math.round(_seconds % 60)
  const representation = `${h ? h + 'h ' : ''}${m ? m + 'min ' : ''}${s ? s + 's' : ''}`.trim()
  return isNegative ? `-${representation}` : representation
}

export function useDateFnLocale(language: MaybeRefOrGetter<string>) {
  return computedAsync(
    async () => {
      const localeString = toValue(language) as keyof typeof dateFnLocales
      let locale = DateFnEnUsLocale
      if (localeString in dateFnLocales) {
        locale = await dateFnLocales[localeString]()
      }
      return locale
    },
    DateFnEnUsLocale as unknown as Locale,
  )
}

export function useRelativeDistanceToNow(
  date: MaybeRefOrGetter<Date | string | null | undefined>,
  options: { includeSeconds?: boolean; addSuffix?: boolean } = {},
) {
  const { locale: appLocale } = useI18n()
  const dateFnLocale = useDateFnLocale(appLocale)
  return computedAsync(async () => {
    const dateValue = toValue(date)
    if (!dateValue) return ''
    return formatDistanceToNow(ensureDate(dateValue), { locale: dateFnLocale.value, ...options })
  }, '')
}

export function useIsToday(
  date: MaybeRefOrGetter<Date | string>,
  today?: MaybeRefOrGetter<Date | string>,
) {
  const datetime = computed(() => ensureDate(toValue(date)))
  const now =
    typeof today === 'undefined'
      ? useNow({ interval: 60 * 1000 })
      : computed(() => ensureDate(toValue(today)))
  return computed(() => {
    return (
      now.value.getDate() === datetime.value.getDate() &&
      now.value.getMonth() === datetime.value.getMonth() &&
      now.value.getFullYear() === datetime.value.getFullYear()
    )
  })
}

function arrayPadLeft<T>(array: T[], maxLength: number, fillValue: T): T[] {
  return Array(Math.max(0, maxLength - array.length))
    .fill(fillValue)
    .concat(array)
}

export function parseTime(time: string): number {
  let [days, hours, minutes, seconds] = Array(4).fill(0)

  if (time.includes(':')) {
    ;[days, hours, minutes, seconds] = arrayPadLeft<number | string>(time.split(':'), 4, 0)
  } else {
    // parses strings like: "3m32", "3 h 2s ", "9d2h3min1"
    const format =
      /^\s*(?:(?<days>\d+)\s*d)?\s*(?:(?<hours>\d+)\s*h)?\s*(?:(?<minutes>\d+)\s*m(?:in)?)?\s*(?:(?<seconds>\d+)\s*s?)?\s*$/i
    ;({ days, hours, minutes, seconds } = time.match(format)?.groups ?? {})
  }

  return (
    parseInt(days || 0) * 24 * 60 * 60 +
    parseInt(hours || 0) * 60 * 60 +
    parseInt(minutes || 0) * 60 +
    parseInt(seconds || 0)
  )
}

export type UseISODateAsDateOptions = {
  default?: MaybeRefOrGetter<Date>
  modify?: (date: Date) => Date
}

export function useISODateAsDate(
  value: Ref<string>,
  options: UseISODateAsDateOptions | undefined = undefined,
) {
  return computed({
    get() {
      let date = parseISO(toValue(value))
      if (isNaN(date.getTime())) date = toValue(options?.default ?? new Date())
      if (options?.modify) date = options?.modify(date)
      return date
    },
    set(date) {
      value.value = formatISO(date, { representation: 'date' })
    },
  })
}

type UseFormattedISODateOptions = {
  representation?: 'date' | 'time' | 'complete' | undefined
  stripOffset?: boolean | undefined
}

export function useFormattedISODate(
  date: Ref<Date>,
  defaultValue?: MaybeRefOrGetter<Date> | undefined,
  options?: UseFormattedISODateOptions | undefined,
): ComputedRef<string>
export function useFormattedISODate(
  date: Ref<Date | null | undefined>,
  defaultValue?: MaybeRefOrGetter<Date> | undefined,
  options?: UseFormattedISODateOptions | undefined,
): ComputedRef<string | null>
export function useFormattedISODate(
  date: Ref<Date> | Ref<Date | null | undefined>,
  defaultValue?: MaybeRefOrGetter<Date> | undefined,
  options?: UseFormattedISODateOptions | undefined,
) {
  const offsetPattern = /(Z|[+-]\d\d:\d\d)$/
  function maybeStripOffset(isoDate: string) {
    return options?.stripOffset ? isoDate.replace(offsetPattern, '') : isoDate
  }

  function maybeAddOffset(isoDate: string, currentDate: Date | undefined) {
    if (!options?.stripOffset) return isoDate
    const match = isoDate.match(offsetPattern)
    if (match) return isoDate
    const _currentDate = currentDate ?? new Date()
    const offset = (formatISO(_currentDate).match(offsetPattern) as RegExpMatchArray)[0]
    return isoDate + offset
  }

  return computed({
    get() {
      return date.value
        ? maybeStripOffset(
            formatISO(date.value, { representation: options?.representation ?? 'complete' }),
          )
        : null
    },
    set(dateValue: string | null | undefined) {
      const _defaultValue = toValue(defaultValue)
      const offsetRefDate = date.value ?? _defaultValue
      if (!dateValue) {
        date.value = _defaultValue ?? null
      } else if (dateValue.includes('T')) {
        date.value = parseISO(maybeAddOffset(dateValue, offsetRefDate))
      } else {
        date.value = parseISO(dateValue)
      }
    },
  }) as Ref<Date> extends typeof date ? ComputedRef<string> : ComputedRef<string | null>
}

export function getClosestSlot(slotDurationMinutes: number, date?: Date): Date {
  date = date ?? new Date()
  const slotDurationMillis = slotDurationMinutes * 60 * 1000
  const slots = Math.floor(date.getTime() / slotDurationMillis)
  const closestSlotTimestamp = slots * slotDurationMillis
  return new Date(closestSlotTimestamp)
}

export function getNextAvailableSlot(slotDurationMinutes: number, date?: Date): Date {
  date = date ?? new Date()
  const slotDurationMillis = slotDurationMinutes * 60 * 1000
  const slots = Math.ceil(date.getTime() / slotDurationMillis)
  const nextSlotTimestamp = slots * slotDurationMillis
  return new Date(nextSlotTimestamp)
}

export function useDuration(
  time: MaybeRefOrGetter<number | { start: string | Date; end: string | Date } | null | undefined>,
) {
  const durationSeconds = computed(() => {
    const _time = toValue(time) ?? 0
    return typeof _time === 'number' ? _time : calculateDurationSeconds(_time.start, _time.end)
  })
  const durationLabel = computed(() => secondsToDurationString(durationSeconds.value))
  return { durationSeconds, durationLabel }
}

export function formatISOWeek(date: string | Date) {
  return format(date, "RRRR-'W'II")
}

type TimeToDateOptions = {
  referenceDate?: Date | string | undefined | null
}

export function timeToDate(localTime: string, options: TimeToDateOptions | undefined = undefined) {
  const referenceDate = ensureDate(options?.referenceDate ?? new Date())
  const dateString = formatISO(referenceDate, { representation: 'date' })
  return parseISO(`${dateString}T${localTime}`)
}

function leadingZero(number: number | string) {
  return String(number).padStart(2, '0')
}

type SecondsToTimeOptions = { noSeconds?: boolean; forceHours?: boolean; sign?: boolean | null }

export function secondsToTime(
  durationInSeconds: number,
  options: SecondsToTimeOptions | undefined = undefined,
) {
  const forceHours = options?.forceHours ?? false
  const noSeconds = options?.noSeconds ?? false
  const sign = options?.sign ?? false

  const absDuration = Math.abs(durationInSeconds)
  const seconds = Math.floor(absDuration % 60)
  let minutes = Math.floor((absDuration % 3600) / 60)
  let hours = Math.floor(absDuration / 3600)

  // Round to the next full minute when noSeconds is true.
  // This is intentionally not >= 30, so that it’s always clear
  // if seconds have been stripped.
  if (noSeconds && seconds > 0) {
    minutes += 1
    // Handle minute overflow
    if (minutes === 60) {
      minutes = 0
      hours += 1
    }
  }

  let result = ''
  if (hours > 0 || forceHours || !noSeconds) result += `${leadingZero(hours)}:`
  result += `${leadingZero(minutes)}`
  if (!noSeconds) result += `:${leadingZero(seconds)}`
  if (sign === null && durationInSeconds < 0) result = '-' + result
  else if (sign) result = (durationInSeconds < 0 ? '-' : '+') + result
  return result
}

export function useTimeToDate(
  time: MaybeRefOrGetter<string | number | null | undefined>,
  options:
    | { referenceDate?: MaybeRefOrGetter<TimeToDateOptions['referenceDate']> | undefined }
    | undefined = undefined,
) {
  return computed(() => {
    const referenceDate = toValue(options?.referenceDate) ?? new Date()
    const _time = toValue(time)
    if (_time === null || _time === undefined) return null
    if (typeof _time === 'number') return addSeconds(referenceDate, _time)
    return timeToDate(_time, { referenceDate })
  })
}

export type UseTimeFormatterDateFormat =
  | Intl.DateTimeFormatOptions['dateStyle']
  | Intl.DateTimeFormatOptions
  | null
export type UseTimeFormatterTimeFormat =
  | Intl.DateTimeFormatOptions['timeStyle']
  | Intl.DateTimeFormatOptions
  | 'auto'
  | null
export type UseTimeFormatterOptions = {
  withDate?: boolean | undefined
  forceSeconds?: boolean | undefined
  dateFormat?: UseTimeFormatterDateFormat
  timeFormat?: UseTimeFormatterTimeFormat
}

export function useTimeFormatter(
  defaultOptions: MaybeRefOrGetter<UseTimeFormatterOptions | undefined> = undefined,
) {
  const { locale } = useI18n()
  const option = <T>(v: T | undefined | null, defaultValue: T) =>
    v === undefined ? defaultValue : v

  return function format(
    date: Date | string,
    options: UseTimeFormatterOptions | undefined = undefined,
  ) {
    const _options = { ...toValue(defaultOptions), ...options }
    const withDate = _options?.withDate ?? false
    const forceSeconds = _options?.forceSeconds ?? false
    const dateFormat = option(_options?.dateFormat, 'medium')
    const timeFormat = option(_options?.timeFormat, 'auto')
    const _date = ensureDate(date)

    const dateOptions: Intl.DateTimeFormatOptions | null = withDate
      ? typeof dateFormat === 'string'
        ? { dateStyle: dateFormat }
        : dateFormat
      : null
    const timeOptions: Intl.DateTimeFormatOptions | null =
      timeFormat === 'auto'
        ? dateOptions?.dateStyle
          ? { timeStyle: _date.getSeconds() || forceSeconds ? 'medium' : 'short' }
          : {
              hour: '2-digit',
              minute: '2-digit',
              second: _date.getSeconds() || forceSeconds ? '2-digit' : undefined,
            }
        : typeof timeFormat === 'string'
          ? { timeStyle: timeFormat }
          : timeFormat
    return _date.toLocaleString(locale.value, { ...dateOptions, ...timeOptions })
  }
}

export function maxDate(...dates: (Date | string | null | undefined)[]) {
  const realDates = dates
    .filter((date) => date !== null && date !== undefined)
    .map((date) => ensureDate(date))
    .filter((date) => !isNaN(date.getTime()))
  const _maxDate = max(realDates)
  return isNaN(_maxDate.getTime()) ? undefined : _maxDate
}

export function useFormattedTime(
  datetime: MaybeRefOrGetter<string | Date | null>,
  options: { displaySeconds?: boolean | 'auto' | undefined } | undefined = undefined,
) {
  const formatTime = useTimeFormatter()
  const displaySeconds = options?.displaySeconds ?? 'auto'
  return computed(() => {
    const date = toValue(datetime)
    if (!date) return null
    return formatTime(date, {
      withDate: false,
      timeFormat: displaySeconds === 'auto' ? 'auto' : 'short',
      forceSeconds: displaySeconds === true,
    })
  })
}

export function useLocalTime(
  time: Ref<string>,
  tz: MaybeRefOrGetter<string>,
): WritableComputedRef<string>
export function useLocalTime(
  time: MaybeRefOrGetter<string>,
  tz: MaybeRefOrGetter<string>,
): ComputedRef<string>
export function useLocalTime(time: MaybeRefOrGetter<string>, tz: MaybeRefOrGetter<string>) {
  function convertToLocalTime() {
    const date = new TZDateMini(toValue(time), toValue(tz))
    return format(date, `yyyy-MM-dd'T'HH:mm:ss`)
  }

  if (isRef(time) && !isReadonly(time)) {
    return computed({
      get: convertToLocalTime,
      set(value: string) {
        const date = new TZDateMini(value, toValue(tz))
        ;(time as Ref<string>).value = formatISO(date)
      },
    })
  }

  return computed(() => convertToLocalTime())
}

export function getISODateString(date: Date | string | null | undefined = undefined) {
  date = date ? ensureDate(date) : new Date()
  return formatISO(date, { representation: 'date' })
}

export function getTimeString(
  date: Date | string | null | undefined = undefined,
  withSeconds = false,
) {
  date = date ? ensureDate(date) : new Date()
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  return `${hours}:${minutes}${withSeconds ? `:${seconds}` : ''}`
}
